import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSkillsRailAddActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/useSkillsRailAddActions";
import {
  workspacePopoverTooltipArrowClassName,
  workspacePopoverTooltipContentClassName,
} from "@/layouts/WorkspaceLayout/components/workspacePopoverTooltipClassName";
import { cn } from "@/utils";
import { FileArchive, PenLine, Plus } from "lucide-react";

/** Shared add-skill items — used by the header plus-menu and its right-click menu. */
export const addSkillMenuItems = (
  createFromScratch: () => void,
  openUploadZipDialog: () => void,
) => (
  <>
    <DropdownMenuItem onClick={createFromScratch}>
      <PenLine />
      Create from scratch
    </DropdownMenuItem>
    <DropdownMenuItem onSelect={openUploadZipDialog}>
      <FileArchive />
      Upload a skill
    </DropdownMenuItem>
  </>
);

/**
 * Header plus-button menu. Actions come from the caller (`useSkillsRailAddActions`)
 * so the same instance backs the plus button and the header's right-click menu,
 * and a single upload-zip dialog is rendered by the caller.
 */
export function SkillsRailAddSkillMenu({
  createFromScratch,
  openUploadZipDialog,
}: {
  createFromScratch: () => void;
  openUploadZipDialog: () => void;
}) {
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="size-6 p-0"
              aria-label="Add Skill"
            >
              <Plus className="size-4" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          sideOffset={4}
          arrowClassName={workspacePopoverTooltipArrowClassName}
          className={cn(
            "px-2.5 py-1.5 text-xs",
            workspacePopoverTooltipContentClassName,
          )}
        >
          Add Skill
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        {addSkillMenuItems(createFromScratch, openUploadZipDialog)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Text-button trigger for rail zero states — same menu as the header plus control. */
export function SkillsRailAddSkillMenuTrigger({
  label,
  disabled,
}: {
  label: string;
  disabled?: boolean;
}) {
  const { createFromScratch, openUploadZipDialog, uploadZipDialog } =
    useSkillsRailAddActions();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="sm" disabled={disabled}>
            <Plus />
            {label}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {addSkillMenuItems(createFromScratch, openUploadZipDialog)}
        </DropdownMenuContent>
      </DropdownMenu>
      {uploadZipDialog}
    </>
  );
}
